import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckRounded,
  CloseRounded,
  DeleteOutlineRounded,
  DriveFileRenameOutlineRounded,
} from "@mui/icons-material";
import AdaptiveConfirm from "../../common/AdaptiveConfirm";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import { useWatchlistDetail } from "./hooks/useWatchlistDetail";
import { useWatchlists } from "./hooks/useWatchlists";
import { useAddStockToWatchlist } from "./hooks/useAddStockToWatchlist";
import { useRemoveStockFromWatchlist } from "./hooks/useRemoveStockFromWatchlist";
import { useRenameWatchlist } from "./hooks/useRenameWatchlist";
import { useDeleteWatchlist } from "./hooks/useDeleteWatchlist";
import SearchBar from "../../common/SearchBar/SearchBar";
import type { SearchResult } from "../TopBar/useGlobalSearch";
import { WatchlistHolding } from "./hooks/useWatchlists";
import WatchlistStockItem from "./WatchlistStockItem";
import { MAX_HOLDINGS_PER_WATCHLIST } from "./watchlistLimits";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { hapticError, hapticSelection, hapticSuccess } from "../../../lib/haptics";
import { overlaySurface } from "../overviewPanel";

const MAX_NAME_LENGTH = 100;

interface ManageWatchlistModalProps {
  open: boolean;
  onClose: () => void;
  watchlistId: string | null;
  /** Lets the dashboard drop its selection once the watchlist is gone. */
  onDeleted?: () => void;
}

/**
 * Everything you can do to one watchlist: rename it, change its stocks, delete
 * it. Management is scoped to the list you're looking at rather than living in
 * the switcher, which stays a plain picker.
 *
 * Full-screen on phones rather than a bottom sheet: this pairs a search field
 * with a list of up to twenty rows, and a sheet would leave the list squeezed
 * between the handle and the keyboard.
 */
const ManageWatchlistModal: React.FC<ManageWatchlistModalProps> = ({
  open,
  onClose,
  watchlistId,
  onDeleted,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isTouch = useTouchLayout();
  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";
  const { trackFeature } = useAnalytics();

  const [searchBarKey, setSearchBarKey] = React.useState(0);
  const [pendingRemoval, setPendingRemoval] = React.useState<WatchlistHolding | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [draftName, setDraftName] = React.useState<string | null>(null);

  const { data: watchlist, isLoading } = useWatchlistDetail(watchlistId);
  const { data: allWatchlists } = useWatchlists();
  const addStock = useAddStockToWatchlist(watchlistId ?? "");
  const removeStock = useRemoveStockFromWatchlist(watchlistId ?? "");
  const renameWatchlist = useRenameWatchlist();
  const deleteWatchlist = useDeleteWatchlist();

  const holdings = watchlist?.holdings ?? [];
  const isFull = holdings.length >= MAX_HOLDINGS_PER_WATCHLIST;
  const isOnlyWatchlist = (allWatchlists?.length ?? 0) <= 1;
  const isRenaming = draftName !== null;

  // Leaving the modal abandons an in-progress rename rather than holding it
  // across opens.
  React.useEffect(() => {
    if (!open) {
      setDraftName(null);
      renameWatchlist.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAddStock = (searchResult: SearchResult) => {
    if (!watchlistId || isFull) return;
    addStock.mutate(
      { ticker: searchResult.symbol },
      { onSuccess: () => hapticSelection(), onError: () => hapticError() },
    );
    trackFeature("watchlist_changed", { action: "add", ticker: searchResult.symbol });
    // Clear the SearchBar by forcing a remount
    setSearchBarKey((prev) => prev + 1);
  };

  const handleConfirmRemove = () => {
    if (!watchlistId || !pendingRemoval) return;
    removeStock.mutate(
      { holdingId: pendingRemoval.id, ticker: pendingRemoval.ticker },
      { onSuccess: () => hapticSelection(), onError: () => hapticError() },
    );
    trackFeature("watchlist_changed", { action: "remove", ticker: pendingRemoval.ticker });
    setPendingRemoval(null);
  };

  const handleStartRename = () => {
    renameWatchlist.reset();
    setDraftName(watchlist?.name ?? "");
  };

  const handleSubmitRename = () => {
    const trimmed = (draftName ?? "").trim();
    if (!watchlistId || trimmed.length === 0) {
      setDraftName(null);
      return;
    }
    if (trimmed === watchlist?.name) {
      setDraftName(null);
      return;
    }
    renameWatchlist.mutate(
      { watchlistId, name: trimmed },
      {
        onSuccess: () => {
          hapticSuccess();
          setDraftName(null);
        },
        onError: () => hapticError(),
      },
    );
  };

  const handleConfirmDelete = () => {
    if (!watchlistId) return;
    deleteWatchlist.mutate(watchlistId, {
      onSuccess: () => {
        hapticSuccess();
        setIsDeleteConfirmOpen(false);
        onDeleted?.();
        onClose();
      },
      onError: () => {
        hapticError();
        setIsDeleteConfirmOpen(false);
      },
    });
  };

  // Accounts predating the cap can hold more than it allows; those keep every
  // stock, so report the real total rather than a nonsensical "23 of 20".
  const countLabel = isFull
    ? `${holdings.length} stocks`
    : `${holdings.length} of ${MAX_HOLDINGS_PER_WATCHLIST} stocks`;
  const searchPlaceholder = isFull ? "Watchlist is full" : "Search stocks to add...";
  const deleteBlockedReason = "You need to keep at least one watchlist";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth={false}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: fullScreen ? "100%" : "min(520px, 94vw)",
              m: 0,
              display: "flex",
              flexDirection: "column",
              maxHeight: fullScreen ? "100%" : "min(680px, 88vh)",
              borderRadius: fullScreen ? 0 : 3,
              backgroundColor: overlaySurface(isDark),
              backgroundImage: "none",
              boxShadow: "none",
              border: fullScreen ? "none" : `1px solid ${hairline}`,
            },
          },
        }}
      >
        {/* Header — the name doubles as the rename control */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: "none",
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 3, sm: 3 },
            pb: 2,
            // Native full-screen: clear the status bar / notch.
            mt: fullScreen ? "env(safe-area-inset-top)" : 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!isRenaming && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Typography
                  component="h2"
                  noWrap
                  sx={{
                    m: 0,
                    fontSize: "1.05rem",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: theme.palette.text.primary,
                  }}
                >
                  {watchlist?.name ?? "Watchlist"}
                </Typography>
                <IconButton
                  onClick={handleStartRename}
                  disabled={!watchlist}
                  aria-label="Rename watchlist"
                  size="small"
                  sx={{
                    flex: "none",
                    width: 28,
                    height: 28,
                    color: theme.palette.text.secondary,
                    opacity: 0.6,
                    transition: "opacity 0.18s ease, color 0.18s ease",
                    "@media (hover: none)": { opacity: 1 },
                    "&:hover": { opacity: 1, color: "#22c55e" },
                    "&:focus-visible": { opacity: 1, outline: "2px solid #22c55e" },
                  }}
                >
                  <DriveFileRenameOutlineRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}

            {isRenaming && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <InputBase
                  autoFocus
                  value={draftName ?? ""}
                  onChange={(event) => setDraftName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleSubmitRename();
                    if (event.key === "Escape") setDraftName(null);
                  }}
                  inputProps={{ maxLength: MAX_NAME_LENGTH, "aria-label": "Watchlist name" }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    border: `1px solid ${hairline}`,
                    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                    fontSize: "1.05rem",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: theme.palette.text.primary,
                    transition: "border-color 0.18s ease",
                    "&.Mui-focused": { borderColor: "rgba(34, 197, 94, 0.5)" },
                  }}
                />
                <IconButton
                  onClick={handleSubmitRename}
                  disabled={renameWatchlist.isPending}
                  aria-label="Save name"
                  size="small"
                  sx={{ flex: "none", width: 28, height: 28, color: "#22c55e" }}
                >
                  {renameWatchlist.isPending ? (
                    <CircularProgress size={14} sx={{ color: "#22c55e" }} />
                  ) : (
                    <CheckRounded sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Box>
            )}

            {!renameWatchlist.error && (
              <Typography
                sx={{ mt: 0.25, fontSize: "0.8rem", color: theme.palette.text.secondary }}
              >
                {countLabel}
              </Typography>
            )}
            {renameWatchlist.error && (
              <Typography sx={{ mt: 0.25, fontSize: "0.8rem", color: theme.palette.error.main }}>
                {renameWatchlist.error.message}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{ flex: "none", mt: -0.5, mr: -0.5 }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Search — pinned, so adding never scrolls out of reach */}
        <Box sx={{ flex: "none", px: { xs: 2.5, sm: 3 }, pb: 2 }}>
          <SearchBar
            key={searchBarKey}
            placeholder={searchPlaceholder}
            width="100%"
            variant="page"
            onSelectItem={handleAddStock}
          />

          {isFull && (
            <Typography sx={{ mt: 1, fontSize: "0.78rem", color: theme.palette.text.secondary }}>
              Remove a stock to make room for another.
            </Typography>
          )}

          {addStock.error && (
            <Typography sx={{ mt: 1, fontSize: "0.78rem", color: theme.palette.error.main }}>
              {addStock.error.message}
            </Typography>
          )}
        </Box>

        {/* Stocks — the only scrolling region */}
        <Box
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2.5, sm: 3 } }}
        >
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={22} sx={{ color: "#22c55e" }} />
            </Box>
          )}

          {!isLoading && holdings.length === 0 && (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 600, color: theme.palette.text.primary }}
              >
                No stocks yet
              </Typography>
              <Typography
                sx={{ mt: 0.5, fontSize: "0.8rem", color: theme.palette.text.secondary }}
              >
                Search above to add your first.
              </Typography>
            </Box>
          )}

          {!isLoading &&
            holdings.map((holding, index) => (
              <WatchlistStockItem
                key={holding.id}
                holding={holding}
                onRemove={setPendingRemoval}
                isRemoving={removeStock.isPending && pendingRemoval?.id === holding.id}
                showDivider={index > 0}
              />
            ))}
        </Box>

        {/* Destructive action, kept apart from everything routine above it */}
        <Box
          sx={{
            flex: "none",
            display: "flex",
            justifyContent: "flex-start",
            px: { xs: 2.5, sm: 3 },
            py: 1.5,
            borderTop: `1px solid ${hairline}`,
            pb: `calc(12px + env(safe-area-inset-bottom))`,
          }}
        >
          <Tooltip title={isOnlyWatchlist ? deleteBlockedReason : ""} placement="top">
            {/* Disabled buttons swallow pointer events, so the span carries the hover */}
            <span>
              <Button
                onClick={() => setIsDeleteConfirmOpen(true)}
                disabled={!watchlist || isOnlyWatchlist}
                startIcon={<DeleteOutlineRounded sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  borderRadius: 1.5,
                  color: theme.palette.error.main,
                  "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" },
                }}
              >
                Delete watchlist
              </Button>
            </span>
          </Tooltip>
          {/* Tooltips never fire on touch, so the reason is stated inline
              there. Keyed on input type rather than viewport width — a tablet
              is wide AND touch, and would otherwise get neither. */}
          {isOnlyWatchlist && isTouch && (
            <Typography
              sx={{
                alignSelf: "center",
                ml: 1,
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
              }}
            >
              {deleteBlockedReason}
            </Typography>
          )}
        </Box>
      </Dialog>

      {/* Never window.confirm — a native dialog freezes the Capacitor WebView. */}
      <AdaptiveConfirm
        open={pendingRemoval !== null}
        title={`Remove ${pendingRemoval?.ticker ?? ""}?`}
        message="This takes it out of this watchlist. Other watchlists keep it."
        confirmLabel="Remove"
        destructive
        loading={removeStock.isPending}
        onConfirm={handleConfirmRemove}
        onCancel={() => setPendingRemoval(null)}
      />

      <AdaptiveConfirm
        open={isDeleteConfirmOpen}
        title={`Delete ${watchlist?.name ?? "watchlist"}?`}
        message="This removes the watchlist and every stock in it. This can't be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteWatchlist.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default ManageWatchlistModal;
